import React, { useEffect, useState } from "react";
import "./PattaCertificate.css";

// Kramantham type to patta image mapping
const KRAMANTHAM_TO_PATTA_IMAGE = {
  'moolam': '/assets/moolam_patta.png',
  'saamaanya_kramamu': '/assets/krama_patta.png',
  'saamaanya_ghanamu': '/assets/ghana_patta.png',
  'vishesha_kramamu': '/assets/visesha_krama_patta.png',
  'vishesha_ghanamu': '/assets/visesha_ghana_patta.png'
};

// Kramantham type to title mapping
const KRAMANTHAM_TO_TITLE = {
  'saamaanya_kramamu': 'क्रमान्तम्',
  'moolam': 'मूलम्',
  'vishesha_kramamu': 'विशेष क्रमान्तम्',
  'saamaanya_ghanamu': 'घनान्तम्',
  'vishesha_ghanamu': 'विशेष घनान्तम्'
};

// Sanskrit certificate content template
// वेदशास्त्रसंवर्धिनीसभया ________ संवत्सरे प्रवर्तितायां परीक्षायां ________ अभिजनः ________ गोत्रजः ________ शिष्यः ________ शर्म्मा ________ श्रेण्यां उत्तीर्णः अभवत् इति सहर्षं प्रमाणीकृत्यते।

export default function PattaCertificate({ data, forPdf = false }) {
  const [examinerSignatureUrls, setExaminerSignatureUrls] = useState({});
  
  console.log("PattaCertificate data:", data);
  console.log("kramantham_type:", data?.kramantham_type);
  console.log("patta_image:", data?.patta_image);
  
  // Determine patta image based on kramantham type - ALWAYS use kramantham_type mapping
  const pattaImage = (data?.kramantham_type && KRAMANTHAM_TO_PATTA_IMAGE[data.kramantham_type]) 
    ? KRAMANTHAM_TO_PATTA_IMAGE[data.kramantham_type]
    : '/assets/moolam_patta.png';
  
  // Fetch signature URLs
  useEffect(() => {
    const fetchSignatureUrls = async () => {
      // Examiner signatures use download endpoints
      if (data?.examiners && Array.isArray(data.examiners)) {
        const urls = {};
        for (const examiner of data.examiners) {
          if (examiner.signature_minio_path || examiner.signature_url) {
            // Use the download endpoint or signature_url if available
            urls[examiner.id] = examiner.signature_url || `/api/parikshaadhikari/download/${examiner.id}/signature`;
          }
        }
        setExaminerSignatureUrls(urls);
        console.log("Examiner signature URLs:", urls);
      }
    };
    
    if (data) {
      fetchSignatureUrls();
    }
  }, [data]);
  
  // Build the Sanskrit certificate text with filled values
  const buildCertificateText = () => {
    const year = data?.telugu_year || data?.teluguYear || '________';
    const prantham = data?.prantham || '________';
    const gothram = data?.gothram || '________';
    const guruvu = data?.guruvu_name || data?.guruName || '________';
    const vatuName = data?.vatu_name || data?.vatuName || '________';
    const shreni = data?.shreni || '________';
    
    // Use inline CSS styles instead of HTML tags for better PDF rendering
    const highlightStyle = 'font-weight: bold; font-style: italic; text-decoration: underline;';

    return `वेदशास्त्रसंवर्धिनीसभया <span style="${highlightStyle}">${year}</span> संवत्सरे प्रवर्तितायां परीक्षायां <span style="${highlightStyle}">${prantham}</span> अभिजनः <span style="${highlightStyle}">${gothram}</span> गोत्रजः <span style="${highlightStyle}">${guruvu}</span> महाभागानां शिष्यः <span style="${highlightStyle}">${vatuName}</span>, <span style="${highlightStyle}">${shreni}</span> श्रेण्यां उत्तीर्णः इति सहर्षं प्रमाणीक्रियते।`;
  };

  if (!data) {
    return <div className="cert-page">No certificate data</div>;
  }

  return (
    <div className={`cert-page ${forPdf ? "for-pdf" : ""}`}>
      {/* PATTA CERTIFICATE BORDER IMAGE - Based on kramantham type */}
      <img
        src={pattaImage}
        className="cert-border"
        alt="certificate border"
        crossOrigin="anonymous"
      />

      {/* CONTENT LAYER */}
      <div className="cert-layer">
        {/* TOP RIGHT PHOTO - Vatu Photo if available */}
        <div className="photo-frame right">
          {data.rightImage || data.vatu_photo_url ? (
            <img
              src={data.rightImage || data.vatu_photo_url}
              alt="vatu"
              crossOrigin="anonymous"
              style={{
                objectFit: data.vatu_photo_object_config?.fit || 'contain',
                objectPosition: data.vatu_photo_object_config?.position || 'center',
              }}
            />
          ) : (
            <div className="photo-placeholder">वटु चित्रम्</div>
          )}
        </div>
        <div className="title-main">
          {KRAMANTHAM_TO_TITLE[data?.kramantham_type] ||
            "वेदशास्त्रसंवर्धिनी सभा"}
        </div>

        {/* CERTIFICATE BODY PARAGRAPH - Sanskrit Content in Center */}
        <div
          className="certificate-body-text"
          dangerouslySetInnerHTML={{ __html: buildCertificateText() }}
        ></div>

        <div className="signatures">
          {data?.examiners && Array.isArray(data.examiners) && (
            <div className="signature-grid">
              {data.examiners.map((examiner, index) => (
                <div key={index} className="signature-cell">
                  <div className="signature-box">
                    {examinerSignatureUrls[examiner.id] ? (
                      <img
                        src={examinerSignatureUrls[examiner.id]}
                        alt={`Examiner ${index + 1}`}
                        crossOrigin="anonymous"
                        className="signature-image"
                      />
                    ) : (
                      <div className="signature-placeholder">
                        
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* QR CODE BOX */}
        <div className="qr-box">
          {(data.qr || data.qr_code_minio_path) && (
            <img
              src={data.qr || `/api/certificate/qr/${data.certificate_id}`}
              alt="qr"
              crossOrigin="anonymous"
            />
          )}
          {(data.verificationId || data.verification_id) && (
            <div className="verification-id">
              {data.verificationId || data.verification_id}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}